import { useTranslation } from "react-i18next";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import ContentLoader from "react-content-loader";
import submit from "submit-form";
import Avatar from "../../../../parts/avatar";
import BackButton from "../../../../parts/BackButton";
import Select from "../../../../parts/inputs/select";
import RecordButtonHeader from "../../../../parts/RecordButtonHeader";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../../utils/get-avatar-url";

const VideoCenter = ({ locals }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const videos = locals.coachVideos;
  const { athlete, organizationCoach, showBack } = locals.query;
  const { activeUser } = locals;

  const referAsClients = activeUser.referAsClients;
  const roleLabel = referAsClients
    ? t("programs.client")
    : t("programs.athlete");
  const webAppTheme = locals.activeUser.webAppTheme || "dark";
  const [selectedView, setSelectedView] = useState("thumbnail");
  const [displayVideos, setDisplayVideos] = useState([]);
  const [displayThumbnails, setDisplayThumbnails] = useState(false);
  const [displayMoreThumbnails, setDisplayMoreThumbnails] = useState(false);
  const [emptyState, setEmptyState] = useState(false);
  const [limit, setLimit] = useState(10);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedList, setSelectedList] = useState([]);
  const [athleteChanged, setAthleteChanged] = useState(false);

  useEffect(() => {
    if (athlete) {
      setDisplayThumbnails(false);
      setDisplayMoreThumbnails(false);
      setDisplayVideos([]);
      setAthleteChanged(true); // This marks the athlete as changed so the video useEffect triggers properly
    }
  }, [athlete]);

  useEffect(() => {
    const setThumbnail = async () => {
      if (displayVideos.length === 0 && athleteChanged) {
        // Only run if athlete changed
        const videosLength = videos.length > limit ? limit : videos.length;

        for (let i = 0; i < videosLength; i++) {
          const video = videos[i];

          try {
            const { oembed } = await import("@loomhq/loom-embed");
            const thumbnail = await oembed(video.sharedUrl);
            const type = video.type;

            setDisplayVideos((previousVideos) => [
              ...previousVideos,
              {
                ...video,
                thumbnail: thumbnail.thumbnail_url,
                title: thumbnail.title,
                type: type || "calendar",
                time: formatDuration(thumbnail.duration),
              },
            ]);
          } catch (error) {
            setDisplayVideos((previousVideos) => [
              ...previousVideos,
              {
                ...video,
                thumbnail: "/img/new-icons/broken-img.svg",
                title: t("videoCenter.deletedVideo"),
                type: "general",
                time: "-",
              },
            ]);
          }
        }
        setDisplayThumbnails(true);
        setAthleteChanged(false); // Reset athlete change tracker
      } else if (displayVideos.length > 0 && !athleteChanged) {
        // Same athlete but videos may have been refetched (e.g. thread updated after comment)
        // Merge latest server data (thread, etc.) into displayVideos, keep cached thumbnail/title/time
        setDisplayVideos((prev) =>
          prev.map((displayVideo) => {
            const updated = videos.find(
              (v) => String(v._id) === String(displayVideo._id)
            );
            if (!updated) return displayVideo;
            return {
              ...updated,
              thumbnail: displayVideo.thumbnail,
              title: displayVideo.title,
              time: displayVideo.time,
            };
          })
        );
        setDisplayMoreThumbnails(true);
      }
    };

    if (videos.length > 0) {
      setThumbnail();
    } else if (videos.length === 0) {
      setDisplayThumbnails(true);
      setEmptyState(true);
    }
  }, [videos, athleteChanged]);

  useEffect(() => {
    const loadMoreVideos = async () => {
      if (displayVideos.length < limit) {
        const videosLengthStart = displayVideos.length;
        const videosLengthEnd = videos.length > limit ? limit : videos.length;

        const newVideos = [...displayVideos];

        for (let i = videosLengthStart; i < videosLengthEnd; i++) {
          const video = videos[i];

          try {
            const { oembed } = await import("@loomhq/loom-embed");
            const thumbnail = await oembed(video.sharedUrl);
            const type = video.type;

            newVideos.push({
              ...video,
              thumbnail: thumbnail.thumbnail_url,
              title: thumbnail.title,
              type: type || "calendar",
              time: formatDuration(thumbnail.duration),
            });
          } catch (error) {
            newVideos.push({
              ...video,
              thumbnail: "/img/new-icons/broken-img.svg",
              title: "Deleted Video",
              type: "general",
              time: "-",
            });
          }
        }

        setDisplayVideos([...newVideos]);
        setDisplayMoreThumbnails(false);
      } else {
        const cuttedVideos = displayVideos.slice(0, limit);
        setDisplayVideos(cuttedVideos);
        setDisplayMoreThumbnails(false);
      }
    };

    if (displayMoreThumbnails) {
      loadMoreVideos();
    }
  }, [limit, videos, displayMoreThumbnails]);

  const handleLoadMore = (newLimit) => {
    const limit = newLimit === "all" ? videos.length : newLimit;

    setDisplayMoreThumbnails(true);
    setLimit(limit);
  };

  const handleDeselectAll = () => {
    setSelectedList([]);
    setAllSelected(false);
  };

  const handleSelectAll = () => {
    const selected = videos.map(({ _id }) => _id);
    setSelectedList(selected);
    setAllSelected(true);
  };

  const handleSelectVideo = (id) => {
    setSelectedList((list) => {
      if (list.indexOf(id) > -1) {
        return list.filter((item) => item !== id);
      } else {
        return [...list, id];
      }
    });
  };

  const handleDelete = () => {
    submit(`/actions/coach-videos/delete/${selectedList}`, {
      method: "GET",
    });

    setSelectedList([]);
    setAllSelected(false);

    const newDisplay = displayVideos.filter(
      (video) => !selectedList.includes(video._id)
    );

    setDisplayVideos(newDisplay);
  };

  const sortByDate = () => {
    const newVideos = [...displayVideos].reverse();

    setDisplayVideos(newVideos);
  };

  return (
    <div role="video-center-wrapper">
      <header>
        <div>
          <div className="flex gap-2 items-center">
            {showBack && <BackButton />}
            <div>
              <h4>{t("profile.coachVideos")}</h4>
              <p className="p5">
                {t("videoCenter.description", { role: roleLabel })}
              </p>
            </div>
          </div>

          {selectedList.length > 0 ? (
            <div>
              <button
                data-button-type={"secondary"}
                onClick={() => handleDeselectAll()}
              >
                {t("common:cancel")}
              </button>
              <button
                data-button-type={"secondary"}
                data-action="delete"
                onClick={() => handleDelete()}
              >
                {t("common:delete")}
              </button>
            </div>
          ) : (
            <div>
              {videos?.length > 0 && (
                <Select
                  name="show-more"
                  title={t("videoCenter.show")}
                  options={getFilteredOptions(videos.length)}
                  onChange={(newLimit) => handleLoadMore(newLimit)}
                  value={limit}
                />
              )}
              <div className="unit-container">
                <button
                  data-button-type={
                    selectedView === "thumbnail" ? "secondary" : "text-white"
                  }
                  onClick={() => setSelectedView("thumbnail")}
                >
                  <SvgIcon src={"/img/new-icons/thumbnail-squares.svg"} />
                </button>
                <button
                  data-button-type={
                    selectedView === "table" ? "secondary" : "text-white"
                  }
                  onClick={() => setSelectedView("table")}
                >
                  <SvgIcon src={"/img/new-icons/row-horizontal.svg"} />
                </button>
              </div>
              <RecordButtonHeader
                locals={locals}
                BUTTON_ID={"video-center-record-button"}
              />
            </div>
          )}
        </div>
      </header>
      {!emptyState ? (
        <div className="video-center-body">
          {selectedView === "thumbnail" ? (
            <section className="thumbnail-container">
              {!displayThumbnails && (
                <ThumbnailLoading webAppTheme={webAppTheme} />
              )}

              <div className="big-videos">
                {displayThumbnails &&
                  displayVideos
                    .slice(0, 2)
                    .map((video) => (
                      <VideoCard
                        video={video}
                        selectedList={selectedList}
                        key={video._id}
                        handleSelectVideo={handleSelectVideo}
                        athleteId={athlete}
                        organizationCoach={organizationCoach}
                        activeUser={activeUser}
                        athleteUser={locals.athlete}
                      />
                    ))}
              </div>
              <div className="small-videos">
                {displayThumbnails &&
                  displayVideos
                    .slice(2)
                    .map((video) => (
                      <VideoCard
                        video={video}
                        key={video._id}
                        selectedList={selectedList}
                        handleSelectVideo={handleSelectVideo}
                        athleteId={athlete}
                        organizationCoach={organizationCoach}
                        activeUser={activeUser}
                        athleteUser={locals.athlete}
                      />
                    ))}
              </div>

              {displayMoreThumbnails && (
                <ThumbnailMoreLoading webAppTheme={webAppTheme} />
              )}
            </section>
          ) : (
            <VideoTable
              webAppTheme={webAppTheme}
              allSelected={allSelected}
              displayMoreThumbnails={displayMoreThumbnails}
              displayThumbnails={displayThumbnails}
              displayVideos={displayVideos}
              handleSelectVideo={handleSelectVideo}
              selectedList={selectedList}
              handleSelectAll={handleSelectAll}
              handleDeselectAll={handleDeselectAll}
              sortByDate={sortByDate}
              locals={locals}
            />
          )}
        </div>
      ) : (
        <div className="video-center-body">
          <p className="font-bold mt-8">{t("videoCenter.noVideosToShow")}</p>
        </div>
      )}
    </div>
  );
};

VideoCenter.propTypes = {
  locals: PropTypes.object,
};

const formatDuration = (seconds) => {
  const roundedSeconds = Math.round(seconds);
  const minutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;

  if (minutes > 0) {
    if (remainingSeconds >= 31) {
      return `${minutes + 1} min`;
    } else {
      return `${minutes} min`;
    }
  } else {
    return `${roundedSeconds} sec`;
  }
};

const footerTitle = {
  "check-in": "Check-in Summary",
  calendar: "Calendar day view",
  general: "-",
};

function getFilteredOptions(arrayLength) {
  const options = [
    { value: 10, label: "10" },
    { value: 30, label: "30" },
    { value: 70, label: "70" },
    { value: 110, label: "110" },
    { value: "all", label: "All" },
  ];

  const filteredOptions = options.filter(
    (option) => option.value === "all" || option.value <= arrayLength
  );

  return filteredOptions;
}

const VideoCard = ({
  video,
  selectedList,
  handleSelectVideo,
  athleteId,
  organizationCoach,
  activeUser,
  athleteUser,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const { locals } = useRillContext();
  const { threadId: queryThreadId, videoRecordId: queryVideoRecordId } =
    locals.query;
  const thumbnailRef = useRef(null);

  useEffect(() => {
    if (
      queryThreadId === video.thread?._id &&
      queryVideoRecordId === video._id
    ) {
      thumbnailRef.current.click();
    }
  }, [queryThreadId, queryVideoRecordId, video._id, video.thread?._id]);

  const isSelected = selectedList.includes(video._id);
  const lastComment = video.thread?.lastComment;
  const lastCommentUser =
    lastComment?.sender ??
    [activeUser, athleteUser].find(
      (user) => user && user._id === lastComment?.senderId
    );
  const [showHover, setShowHover] = useState(false);
  const [clicked, setClicked] = useState(false);

  let redirectUrl = "";

  if (video.type === "calendar") {
    redirectUrl = organizationCoach
      ? `/athlete-portal/calendar/?athlete=${athleteId}&organizationCoach=${organizationCoach}&month=${moment(
          video.reportDate || video.date
        ).format("YYYY-MM")}&view=day&date=${video.reportDate || video.date}`
      : `/athlete-portal/calendar/?athlete=${athleteId}&month=${moment(
          video.reportDate || video.date
        ).format("YYYY-MM")}&view=day&date=${video.reportDate || video.date}`;
  } else if (video.type === "check-in") {
    redirectUrl = organizationCoach
      ? `/athlete-portal/metrics/check-in-summary?athlete=${athleteId}&organizationCoach=${organizationCoach}&checkInDate=${
          video.reportDate || video.date
        }`
      : `/athlete-portal/metrics/check-in-summary?athlete=${athleteId}&checkInDate=${
          video.reportDate || video.date
        }`;
  }

  return (
    <article
      className="video-card"
      onMouseEnter={() => {
        setShowHover(true);
      }}
      onMouseLeave={() => {
        setShowHover(false);
        setClicked(false);
      }}
    >
      <header>
        {(showHover || isSelected) && (
          <SvgIcon
            src={
              isSelected
                ? "/img/new-icons/checked-square.svg"
                : "/img/new-icons/unchecked-square-white.svg"
            }
            onClick={() => handleSelectVideo(video._id)}
            classNames="header-icon"
            noFilterOnLight
          />
        )}

        {(showHover || isSelected) && (
          <ToolTip
            id={`copy-video-${video._id}-thumbnail`}
            body={clicked ? t("videoCenter.copied") : t("videoCenter.copyLink")}
          >
            <div
              className="hover-icon"
              onClick={() => {
                setClicked(true);
                navigator.clipboard.writeText(video.sharedUrl);
              }}
            >
              <SvgIcon
                className="play-icon"
                src="/img/new-icons/copy-white.svg"
                alt="play"
              />
            </div>
          </ToolTip>
        )}
        <img
          ref={thumbnailRef}
          className={"thumbnail cursor-pointer"}
          src={video.thumbnail}
          loading="lazy"
          onClick={() => {
            submit("/actions/refresh?", {
              method: "GET",
              body: {
                "modal[type]": "watch-loom-video",
                threadId: video.thread?._id,
                videoRecordId: video._id,
                sharedUrl: video.sharedUrl.split(
                  "https://www.loom.com/share/"
                )[1],
                utcDate: moment(video.date, "YYYY-MM-DD").format("YYYYMMDD"),
              },
            });
          }}
        />
        <div className="duration-display">{video.time}</div>
        {lastComment?.message && (
          <div className="last-comment-wrapper">
            <Avatar user={lastCommentUser} size={28} />
            <div className="last-comment-message-wrapper">
              <span className="last-comment-message">
                {lastComment.message}
              </span>
              <div className="message-bubble-arrow" />
            </div>
          </div>
        )}
      </header>
      <div className="body">
        <div className="title">
          <p className="font-bold">{video.title}</p>
          <p className="p5">
            {moment(video.date, "YYYY-MM-DD").format("MMM D, YYYY")}
          </p>
        </div>

        {video.type !== "general" && (
          <a href={redirectUrl}>
            <footer>
              <div className="icon-wrapper">
                <SvgIcon
                  src={
                    video.type === "calendar"
                      ? "/img/new-icons/Calendar-Dark.svg"
                      : "/img/new-icons/clipboard-notes.svg"
                  }
                />
              </div>

              <div>
                <p className="s1">{footerTitle[video.type]}</p>
                <p className="p5">
                  {t("videoCenter.reportDateLabel")}{" "}
                  {moment(video.reportDate || video.date, "YYYY-MM-DD").format(
                    "MMM D, YYYY"
                  )}{" "}
                </p>
              </div>
            </footer>
          </a>
        )}
      </div>
    </article>
  );
};

VideoCard.propTypes = {
  video: PropTypes.object.isRequired,
  selectedList: PropTypes.array.isRequired,
  handleSelectVideo: PropTypes.func.isRequired,
  athleteId: PropTypes.string.isRequired,
  organizationCoach: PropTypes.string.isRequired,
  activeUser: PropTypes.object,
  athleteUser: PropTypes.object,
};

const VideoTable = ({
  webAppTheme,
  displayThumbnails,
  allSelected,
  displayVideos,
  handleSelectVideo,
  selectedList,
  displayMoreThumbnails,
  handleSelectAll,
  handleDeselectAll,
  sortByDate,
  locals,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  return (
    <section role="table-container">
      {!displayThumbnails && <TableLoading webAppTheme={webAppTheme} />}

      <section role="table-wrapper">
        {displayThumbnails && (
          <table role="report-table">
            <thead>
              <tr>
                <th className="select-cell">
                  <SvgIcon
                    src={
                      allSelected
                        ? "/img/new-icons/checked-square.svg"
                        : "/img/new-icons/unchecked-square.svg"
                    }
                    onClickFn={() =>
                      allSelected ? handleDeselectAll() : handleSelectAll()
                    }
                    noFilterOnLight={allSelected}
                  />
                </th>
                <th onClick={() => sortByDate()}>
                  {t("videoCenter.tableDate")}{" "}
                  <SvgIcon src="/img/svg/up_down_arrows.svg" />
                </th>
                <th>{t("videoCenter.tableTitle")}</th>
                <th>{t("videoCenter.tablePage")}</th>
                <th>{t("videoCenter.tableReportDate")}</th>
                <th>{t("videoCenter.tableDuration")}</th>
                <th>{t("videoCenter.tableAction")}</th>
              </tr>
            </thead>
            <tbody>
              {displayVideos.length > 0 &&
                displayVideos.map((video, index) => (
                  <TableRow
                    locals={locals}
                    video={video}
                    handleSelectVideo={handleSelectVideo}
                    selectedList={selectedList}
                    key={`${video._id}-table-row-${index}`}
                  />
                ))}
            </tbody>
          </table>
        )}
      </section>

      {displayMoreThumbnails && <TableLoadingMore webAppTheme={webAppTheme} />}
    </section>
  );
};

const TableRow = ({ video, selectedList, handleSelectVideo, locals }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const isSelected = selectedList.includes(video._id);
  const [clicked, setClicked] = useState(false);

  const lastComment = video.thread?.lastComment;
  const lastCommentUser =
    lastComment?.sender ??
    [locals.athlete, locals.activeUser].find(
      (user) => user && user._id === lastComment?.senderId
    );
  const lastCommentUserAvatar = getAvatarUrl(lastCommentUser);

  return (
    <tr key={video._id} onClick={() => handleSelectVideo(video._id)}>
      <td>
        <SvgIcon
          classNames="cursor-pointer"
          src={
            isSelected
              ? "/img/new-icons/checked-square.svg"
              : "/img/new-icons/unchecked-square.svg"
          }
          noFilterOnLight={isSelected}
        />
      </td>
      <td>{moment(video.date, "YYYY-MM-DD").format("MMM D, YYYY")}</td>
      <td>{video.title}</td>
      <td>{footerTitle[video.type]}</td>
      <td>
        {video.type === "general"
          ? "-"
          : moment(video.reportDate || video.date, "YYYY-MM-DD").format(
              "MMM D, YYYY"
            )}
      </td>
      <td>{video.time}</td>
      <td>
        <div className="action-cell">
          <div className="play-icon-wrapper">
            <SvgIcon
              classNames={`play-icon${
                video?.thread ? " threaded" : ""
              } cursor-pointer`}
              src="/img/new-icons/play-with-gradient.svg"
              alt="play"
              noFilterOnLight={true}
              onClickFn={() => {
                submit("/actions/refresh?", {
                  method: "GET",
                  body: {
                    "modal[type]": "watch-loom-video",
                    threadId: video.thread?._id,
                    videoRecordId: video._id,
                    sharedUrl: video.sharedUrl.split(
                      "https://www.loom.com/share/"
                    )[1],
                    utcDate: moment(video.date, "YYYY-MM-DD").format(
                      "YYYYMMDD"
                    ),
                  },
                });
              }}
            />
            {video?.thread && (
              <img
                className="avatar"
                src={lastCommentUserAvatar}
                alt={`last comment user avatar`}
                loading="lazy"
              />
            )}
          </div>

          <div className="icon-wrapper">
            <ToolTip
              id={`copy-video-${video._id}-table`}
              body={
                clicked ? t("videoCenter.copied") : t("videoCenter.copyLink")
              }
            >
              <SvgIcon
                className="play-icon"
                src="/img/new-icons/copy-white.svg"
                alt="play"
                onClickFn={() => {
                  setClicked(true);
                  navigator.clipboard.writeText(video.sharedUrl);
                }}
              />
            </ToolTip>
          </div>
        </div>
      </td>
    </tr>
  );
};

const ThumbnailLoading = ({ webAppTheme }) => {
  const backgroundColor = webAppTheme === "dark" ? "#363C43" : "#F1F3F6";
  const foregroundColor = webAppTheme === "dark" ? "#40464D" : "#E0E4EA";

  return (
    <ContentLoader
      viewBox="0 0 1360 750"
      height={750}
      width={1360}
      backgroundColor={backgroundColor}
      foregroundColor={foregroundColor}
      speed={1}
      title="Loading Videos..."
    >
      {/* Two big videos */}
      <rect x="30" y="20" rx="8" ry="8" width="550" height="330" />
      <rect x="30" y="365" rx="0" ry="0" width="500" height="18" />
      <rect x="30" y="405" rx="0" ry="0" width="320" height="20" />
      <rect x="600" y="20" rx="8" ry="8" width="550" height="330" />
      <rect x="600" y="365" rx="0" ry="0" width="500" height="18" />
      <rect x="600" y="405" rx="0" ry="0" width="320" height="20" />
      {/* Four small videos  */}
      <rect x="30" y="470" rx="8" ry="8" width="265" height="200" />
      <rect x="30" y="700" rx="0" ry="0" width="200" height="18" />
      <rect x="30" y="725" rx="0" ry="0" width="120" height="20" />

      <rect x="315" y="470" rx="8" ry="8" width="265" height="200" />
      <rect x="315" y="700" rx="0" ry="0" width="200" height="18" />
      <rect x="315" y="725" rx="0" ry="0" width="120" height="20" />

      <rect x="601" y="470" rx="8" ry="8" width="265" height="200" />
      <rect x="601" y="700" rx="0" ry="0" width="200" height="18" />
      <rect x="601" y="725" rx="0" ry="0" width="120" height="20" />

      <rect x="886" y="470" rx="8" ry="8" width="265" height="200" />
      <rect x="886" y="700" rx="0" ry="0" width="200" height="18" />
      <rect x="886" y="725" rx="0" ry="0" width="120" height="20" />
    </ContentLoader>
  );
};

const ThumbnailMoreLoading = ({ webAppTheme }) => {
  const backgroundColor = webAppTheme === "dark" ? "#363C43" : "#F1F3F6";
  const foregroundColor = webAppTheme === "dark" ? "#40464D" : "#E0E4EA";

  return (
    <ContentLoader
      viewBox="0 0 1360 650"
      height={650}
      width={1360}
      backgroundColor={backgroundColor}
      foregroundColor={foregroundColor}
      speed={1}
      title="Loading Videos..."
    >
      {/* Four small videos  */}
      <rect x="30" y="20" rx="8" ry="8" width="265" height="200" />
      <rect x="30" y="250" rx="0" ry="0" width="200" height="18" />
      <rect x="30" y="275" rx="0" ry="0" width="120" height="20" />

      <rect x="315" y="20" rx="8" ry="8" width="265" height="200" />
      <rect x="315" y="250" rx="0" ry="0" width="200" height="18" />
      <rect x="315" y="275" rx="0" ry="0" width="120" height="20" />

      <rect x="601" y="20" rx="8" ry="8" width="265" height="200" />
      <rect x="601" y="250" rx="0" ry="0" width="200" height="18" />
      <rect x="601" y="275" rx="0" ry="0" width="120" height="20" />

      <rect x="886" y="20" rx="8" ry="8" width="265" height="200" />
      <rect x="886" y="250" rx="0" ry="0" width="200" height="18" />
      <rect x="886" y="275" rx="0" ry="0" width="120" height="20" />

      {/* Four small videos  */}
      <rect x="30" y="340" rx="8" ry="8" width="265" height="200" />
      <rect x="30" y="570" rx="0" ry="0" width="200" height="18" />
      <rect x="30" y="595" rx="0" ry="0" width="120" height="20" />

      <rect x="315" y="340" rx="8" ry="8" width="265" height="200" />
      <rect x="315" y="570" rx="0" ry="0" width="200" height="18" />
      <rect x="315" y="595" rx="0" ry="0" width="120" height="20" />

      <rect x="601" y="340" rx="8" ry="8" width="265" height="200" />
      <rect x="601" y="570" rx="0" ry="0" width="200" height="18" />
      <rect x="601" y="595" rx="0" ry="0" width="120" height="20" />

      <rect x="886" y="340" rx="8" ry="8" width="265" height="200" />
      <rect x="886" y="570" rx="0" ry="0" width="200" height="18" />
      <rect x="886" y="595" rx="0" ry="0" width="120" height="20" />
    </ContentLoader>
  );
};

const TableLoading = (props) => {
  const { webAppTheme } = props;

  const backgroundColor = webAppTheme === "dark" ? "#363C43" : "#F1F3F6";
  const foregroundColor = webAppTheme === "dark" ? "#40464D" : "#E0E4EA";

  return (
    <ContentLoader
      width={1240}
      height={550}
      viewBox="0 0 1240 550"
      backgroundColor={backgroundColor}
      foregroundColor={foregroundColor}
      {...props}
    >
      <rect x="63" y="45" rx="3" ry="3" width="1124" height="17" />

      <circle cx="1090" cy="123" r="11" />
      <circle cx="1134" cy="123" r="11" />
      <rect x="129" y="115" rx="3" ry="3" width="175" height="15" />
      <rect x="378" y="114" rx="3" ry="3" width="363" height="15" />
      <rect x="803" y="114" rx="3" ry="3" width="175" height="15" />

      <rect x="68" y="155" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1091" cy="184" r="11" />
      <circle cx="1135" cy="184" r="11" />
      <rect x="130" y="176" rx="3" ry="3" width="175" height="15" />
      <rect x="379" y="175" rx="3" ry="3" width="363" height="15" />
      <rect x="804" y="175" rx="3" ry="3" width="175" height="15" />

      <rect x="69" y="216" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1092" cy="242" r="11" />
      <circle cx="1136" cy="242" r="11" />
      <rect x="131" y="234" rx="3" ry="3" width="175" height="15" />
      <rect x="380" y="233" rx="3" ry="3" width="363" height="15" />
      <rect x="805" y="233" rx="3" ry="3" width="175" height="15" />

      <rect x="70" y="274" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1093" cy="303" r="11" />
      <circle cx="1137" cy="303" r="11" />
      <rect x="132" y="295" rx="3" ry="3" width="175" height="15" />
      <rect x="381" y="294" rx="3" ry="3" width="363" height="15" />
      <rect x="806" y="294" rx="3" ry="3" width="175" height="15" />

      <rect x="71" y="335" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1092" cy="363" r="11" />
      <circle cx="1136" cy="363" r="11" />
      <rect x="131" y="355" rx="3" ry="3" width="175" height="15" />
      <rect x="380" y="354" rx="3" ry="3" width="363" height="15" />
      <rect x="805" y="354" rx="3" ry="3" width="175" height="15" />

      <rect x="70" y="395" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1093" cy="424" r="11" />
      <circle cx="1137" cy="424" r="11" />
      <rect x="132" y="416" rx="3" ry="3" width="175" height="15" />
      <rect x="381" y="415" rx="3" ry="3" width="363" height="15" />
      <rect x="806" y="415" rx="3" ry="3" width="175" height="15" />

      <rect x="68" y="453" rx="3" ry="3" width="1120" height="2" />

      <rect x="63" y="49" rx="3" ry="3" width="2" height="465" />
      <rect x="1186" y="49" rx="3" ry="3" width="2" height="465" />

      <circle cx="1093" cy="484" r="11" />
      <circle cx="1137" cy="484" r="11" />

      <rect x="132" y="476" rx="3" ry="3" width="175" height="15" />
      <rect x="381" y="475" rx="3" ry="3" width="363" height="15" />
      <rect x="806" y="475" rx="3" ry="3" width="175" height="15" />
      <rect x="68" y="513" rx="3" ry="3" width="1120" height="2" />

      {/* "Header" of the table */}
      <rect x="65" y="80" rx="3" ry="3" width="1124" height="17" />
      <rect x="66" y="57" rx="3" ry="3" width="84" height="33" />
      <rect x="271" y="54" rx="3" ry="3" width="182" height="33" />
      <rect x="651" y="55" rx="3" ry="3" width="167" height="33" />
      <rect x="961" y="56" rx="3" ry="3" width="88" height="33" />
      <rect x="1149" y="54" rx="3" ry="3" width="40" height="33" />
    </ContentLoader>
  );
};

const TableLoadingMore = (props) => {
  const { webAppTheme } = props;

  const backgroundColor = webAppTheme === "dark" ? "#363C43" : "#F1F3F6";
  const foregroundColor = webAppTheme === "dark" ? "#40464D" : "#E0E4EA";

  return (
    <ContentLoader
      width={1240}
      height={550}
      viewBox="0 0 1240 550"
      backgroundColor={backgroundColor}
      foregroundColor={foregroundColor}
      {...props}
    >
      <rect x="63" y="30" rx="3" ry="3" width="1124" height="2" />

      <circle cx="1090" cy="63" r="11" />
      <circle cx="1134" cy="63" r="11" />
      <rect x="129" y="55" rx="3" ry="3" width="175" height="15" />
      <rect x="378" y="54" rx="3" ry="3" width="363" height="15" />
      <rect x="803" y="54" rx="3" ry="3" width="175" height="15" />

      <rect x="68" y="95" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1091" cy="124" r="11" />
      <circle cx="1135" cy="124" r="11" />
      <rect x="130" y="116" rx="3" ry="3" width="175" height="15" />
      <rect x="379" y="115" rx="3" ry="3" width="363" height="15" />
      <rect x="804" y="115" rx="3" ry="3" width="175" height="15" />

      <rect x="69" y="156" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1092" cy="182" r="11" />
      <circle cx="1136" cy="182" r="11" />
      <rect x="131" y="174" rx="3" ry="3" width="175" height="15" />
      <rect x="380" y="173" rx="3" ry="3" width="363" height="15" />
      <rect x="805" y="173" rx="3" ry="3" width="175" height="15" />

      <rect x="70" y="214" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1093" cy="243" r="11" />
      <circle cx="1137" cy="243" r="11" />
      <rect x="132" y="235" rx="3" ry="3" width="175" height="15" />
      <rect x="381" y="234" rx="3" ry="3" width="363" height="15" />
      <rect x="806" y="234" rx="3" ry="3" width="175" height="15" />

      <rect x="71" y="275" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1092" cy="303" r="11" />
      <circle cx="1136" cy="303" r="11" />
      <rect x="131" y="295" rx="3" ry="3" width="175" height="15" />
      <rect x="380" y="294" rx="3" ry="3" width="363" height="15" />
      <rect x="805" y="294" rx="3" ry="3" width="175" height="15" />

      <rect x="70" y="335" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1093" cy="363" r="11" />
      <circle cx="1137" cy="363" r="11" />
      <rect x="132" y="355" rx="3" ry="3" width="175" height="15" />
      <rect x="381" y="354" rx="3" ry="3" width="363" height="15" />
      <rect x="806" y="354" rx="3" ry="3" width="175" height="15" />

      <rect x="70" y="395" rx="3" ry="3" width="1120" height="2" />

      <circle cx="1093" cy="424" r="11" />
      <circle cx="1137" cy="424" r="11" />
      <rect x="132" y="416" rx="3" ry="3" width="175" height="15" />
      <rect x="381" y="415" rx="3" ry="3" width="363" height="15" />
      <rect x="806" y="415" rx="3" ry="3" width="175" height="15" />

      <rect x="68" y="453" rx="3" ry="3" width="1120" height="2" />

      <rect x="63" y="34" rx="3" ry="3" width="2" height="420" />
      <rect x="1186" y="34" rx="3" ry="3" width="2" height="420" />

      <circle cx="1093" cy="424" r="11" />
      <circle cx="1137" cy="424" r="11" />

      <rect x="132" y="416" rx="3" ry="3" width="175" height="15" />
      <rect x="68" y="453" rx="3" ry="3" width="1120" height="2" />
    </ContentLoader>
  );
};

export default VideoCenter;
